import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { esRechazoPorTasa, REVERSA_EXPLICITA } from 'src/app/shared/utils/error-http-real';
import { esTitularVesChileno } from 'src/app/shared/utils/saldo-ves';
import { CartolaService } from '../../services/cartola.service';
import {
  CompraVesClpResultado,
  CotizacionCompraVesClp,
  WalletVesClientService,
} from '../../services/wallet-ves-client.service';

/** UUID v4 para la idempotencia de la compra. Fallback para WebViews viejos. */
function nuevoRequestId(): string {
  const c: any = typeof crypto !== 'undefined' ? crypto : null;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

type Paso = 'monto' | 'confirmar' | 'listo';

/**
 * Comprar bolívares con el saldo CLP (solo chilenos con saldo VES).
 *
 * La tasa la pone el servidor: la pantalla muestra su cotización y la manda
 * de vuelta como `tasaCotizada`. Si cambió, el servidor responde 409 y se
 * recotiza. El `clientRequestId` se crea al confirmar y se reutiliza mientras
 * la compra siga incierta (también tras "Volver"), para que reintentar no
 * compre dos veces. Solo se renueva tras un éxito o un rechazo definitivo.
 *
 * El saldo CLP que se muestra es el último de la cartola: el servidor además
 * descuenta las operaciones CLP que todavía están en vuelo, así que el
 * disponible real puede ser algo menor.
 */
@Component({
  selector: 'app-comprar-bolivares',
  templateUrl: './comprar-bolivares.component.html',
  styleUrls: ['./comprar-bolivares.component.sass'],
})
export class ComprarBolivaresComponent implements OnInit, OnDestroy {
  paso: Paso = 'monto';
  montoClp: number | null = null;
  saldoClp = 0;
  saldoVes = 0;
  cotizacion: CotizacionCompraVesClp | null = null;
  cotizando = false;
  comprando = false;
  resultado: CompraVesClpResultado | null = null;
  error = '';
  aviso = '';
  /** La última compra quedó sin respuesta clara: puede haberse hecho. */
  incierto = false;

  private usuarioId: number | null = null;
  private clientRequestId: string | null = null;
  private cotizar$ = new Subject<number>();
  private subs = new Subscription();

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private cartolaService: CartolaService,
    private walletVesClientService: WalletVesClientService,
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.store.select('auth').subscribe(({ user }: any) => {
        if (!user) return;
        if (!esTitularVesChileno(user.tipoId)) {
          this.router.navigate(['/wallet/home']);
          return;
        }
        if (this.usuarioId === user.usuarioId) return;
        this.usuarioId = user.usuarioId;
        this.cargarSaldos();
      }),
    );
    this.subs.add(
      this.cotizar$.pipe(debounceTime(400)).subscribe((monto) => this.cotizar(monto)),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  async cargarSaldos(): Promise<void> {
    if (!this.usuarioId) return;
    try {
      const { saldo } = await this.cartolaService.getSaldoCliente(String(this.usuarioId));
      this.saldoClp = Number(saldo) || 0;
    } catch {
      this.saldoClp = 0;
    }
    this.walletVesClientService.getMyBalance().subscribe({
      // Sin movimientos VES el saldo es 0.
      next: (res) => (this.saldoVes = Number(res?.data?.balanceVes) || 0),
      error: () => (this.saldoVes = 0),
    });
  }

  onMontoChange(): void {
    this.error = '';
    this.aviso = '';
    this.cotizacion = null;
    const monto = Number(this.montoClp) || 0;
    if (monto > 0) this.cotizar$.next(monto);
  }

  async cotizar(monto: number): Promise<void> {
    this.cotizando = true;
    try {
      const res = await firstValueFrom(this.walletVesClientService.cotizarCompraVesClp(monto));
      // Si el monto cambió mientras cotizaba, esa cotización ya no sirve.
      if (monto !== (Number(this.montoClp) || 0)) return;
      this.cotizacion = res?.data ?? null;
    } catch (e: any) {
      this.cotizacion = null;
      this.error = e?.message || 'No se pudo cotizar la compra de bolívares.';
    } finally {
      this.cotizando = false;
    }
  }

  get montoInvalido(): string {
    const monto = Number(this.montoClp) || 0;
    if (monto <= 0) return '';
    if (monto > this.saldoClp) return 'El monto supera tu saldo CLP disponible.';
    const c = this.cotizacion;
    if (c?.montoMinimoClp && monto < c.montoMinimoClp) {
      return `El monto mínimo es $${this.formatClp(c.montoMinimoClp)} CLP.`;
    }
    if (c?.montoMaximoClp && monto > c.montoMaximoClp) {
      return `El monto máximo es $${this.formatClp(c.montoMaximoClp)} CLP.`;
    }
    return '';
  }

  get puedeContinuar(): boolean {
    return !!this.cotizacion && !this.cotizando && !this.montoInvalido && (Number(this.montoClp) || 0) > 0;
  }

  irAConfirmar(): void {
    if (!this.puedeContinuar) return;
    this.error = '';
    this.paso = 'confirmar';
  }

  volver(): void {
    if (this.comprando) return;
    this.paso = 'monto';
    this.aviso = '';
    // Con la compra incierta se conserva el id (y el aviso): si se hizo, el
    // próximo intento devuelve esa misma compra en vez de comprar otra vez.
    if (!this.incierto) this.clientRequestId = null;
  }

  /** MÁX: el servidor exige CLP entero, y nunca más de lo que hay. */
  usarMaximo(): void {
    this.montoClp = Math.max(0, Math.floor(this.saldoClp));
    this.onMontoChange();
  }

  /** Rechazo definitivo: no quedó compra hecha, el próximo intento es otro. */
  private cerrarIntento(): void {
    this.clientRequestId = null;
    this.incierto = false;
  }

  async confirmar(): Promise<void> {
    if (this.comprando || !this.cotizacion) return;
    this.comprando = true;
    this.error = '';
    this.aviso = '';
    // Mismo id si la respuesta anterior fue incierta: el servidor no duplica.
    this.clientRequestId = this.clientRequestId ?? nuevoRequestId();
    try {
      const res = await firstValueFrom(
        this.walletVesClientService.compraVesClp({
          montoClp: Number(this.montoClp),
          tasaCotizada: this.cotizacion.tasa,
          clientRequestId: this.clientRequestId,
        }),
      );
      this.resultado = res?.data ?? null;
      if (this.resultado) this.saldoVes = Number(this.resultado.balanceVes) || this.saldoVes;
      this.clientRequestId = null;
      this.incierto = false;
      this.paso = 'listo';
      this.cargarSaldos();
    } catch (e: any) {
      const status: number | undefined = e?.status;
      const mensaje: string = e?.message || 'No se pudo comprar bolívares.';
      if (e?.transitorio) {
        // Pudo haberse hecho: no se crea otro id hasta que el cliente vuelva.
        this.incierto = true;
        this.error =
          'No tuvimos respuesta de la compra. Revisa tus movimientos VES antes de intentar de nuevo; si reintentas desde aquí no se cobrará dos veces.';
      } else if (REVERSA_EXPLICITA.test(mensaje)) {
        // 409 (o 5xx) «revertida»: la compra con este id no se hizo y no se
        // descontó CLP. Es definitivo: un intento nuevo lleva otro id.
        this.cerrarIntento();
        this.error = mensaje;
        this.cargarSaldos();
      } else if (esRechazoPorTasa(status, mensaje)) {
        // El replay va antes que la tasa: si responde «tasa», no hubo compra.
        this.cerrarIntento();
        this.aviso = 'La tasa cambió. Revisa la nueva cotización y confirma de nuevo.';
        await this.cotizar(Number(this.montoClp) || 0);
        if (!this.cotizacion) {
          // Sin cotización nueva el paso confirmar no tiene qué mostrar.
          this.aviso = '';
          this.error = this.error || 'No se pudo actualizar la cotización. Intenta de nuevo.';
          this.paso = 'monto';
        }
      } else {
        this.cerrarIntento();
        this.error = mensaje;
      }
    } finally {
      this.comprando = false;
    }
  }

  nuevaCompra(): void {
    this.cerrarIntento();
    this.paso = 'monto';
    this.montoClp = null;
    this.cotizacion = null;
    this.resultado = null;
    this.error = '';
    this.aviso = '';
  }

  irAMovimientos(): void {
    this.router.navigate(['/wallet/client/orders'], { queryParams: { moneda: 'VES' } });
  }

  /** Salidas del saldo VES ofrecidas tras la compra (retiro, VES→CLP, VES→USDT). */
  irA(ruta: string, queryParams?: Record<string, string>): void {
    this.router.navigate([ruta], queryParams ? { queryParams } : {});
  }

  formatClp(valor: number | null | undefined): string {
    return Number(valor || 0).toLocaleString('es-CL', { maximumFractionDigits: 0 });
  }

  formatVes(valor: number | null | undefined): string {
    return Number(valor || 0).toLocaleString('es-VE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
}
